import { useState, useEffect } from 'react';
import { Target, Activity, ClipboardCheck, Trophy, BookOpen, ChevronDown, ChevronUp, CheckCircle, Clock, X } from '../icons';
import { LoadingSpinner } from '../LoadingSpinner';
import {
  fetchGoalsForUser, fetchHabitsForUser, fetchTasksForUser,
  fetchCheckInsForUser, fetchMilestonesForUser, fetchJournalEntriesForUser,
  type Goal, type Habit, type Task, type CheckIn, type Milestone, type JournalEntry,
} from '../../lib/progress';
import { CoachOutcomesSection } from './CoachOutcomesSection';

const STATUS_LABELS: Record<Goal['status'], string> = {
  active: 'Active',
  completed: 'Completed',
  paused: 'Paused',
  abandoned: 'Abandoned',
};

const STATUS_STYLES: Record<Goal['status'], string> = {
  active: 'bg-sage-100 text-sage-700',
  completed: 'bg-blue-100 text-blue-700',
  paused: 'bg-amber-100 text-amber-700',
  abandoned: 'bg-red-100 text-red-700',
};

interface GoalGroupProps {
  title: string;
  goals: Goal[];
  goalMilestonesMap: Map<string, Milestone[]>;
  defaultOpen?: boolean;
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
}

function GoalGroup({ title, goals, goalMilestonesMap, defaultOpen = true, icon: Icon, iconColor }: GoalGroupProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  if (goals.length === 0) return null;

  return (
    <div>
      <button
        onClick={() => setIsOpen(v => !v)}
        className="flex items-center gap-2 mb-3 w-full text-left group"
      >
        <Icon className={`w-4 h-4 ${iconColor} flex-shrink-0`} />
        <span className="text-sm font-bold text-chocolate-800">{title}</span>
        <span className="text-xs text-chocolate-400">({goals.length})</span>
        <span className="ml-auto text-chocolate-400 group-hover:text-chocolate-600 transition-colors">
          {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>
      {isOpen && (
        <div className="space-y-2">
          {goals.map(g => {
            const goalMilestones = goalMilestonesMap.get(g.id) || [];
            return (
              <div key={g.id} className="freud-card-elevated p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLES[g.status]}`}>{STATUS_LABELS[g.status]}</span>
                  <span className={`text-sm font-medium text-chocolate-900 ${g.status === 'abandoned' ? 'line-through text-chocolate-400' : ''}`}>{g.title}</span>
                  {g.target_date && <span className="text-[10px] text-chocolate-400 ml-auto flex-shrink-0">by {new Date(g.target_date).toLocaleDateString()}</span>}
                </div>
                {g.description && <p className="text-xs text-chocolate-500 mt-1">{g.description}</p>}
                {goalMilestones.length > 0 && (
                  <div className="mt-2 space-y-1.5 pl-2 border-l-2 border-yellow-200">
                    {goalMilestones.map(m => (
                      <div key={m.id} className="flex items-center gap-2">
                        <Trophy className="w-3 h-3 text-yellow-500 flex-shrink-0" />
                        <span className="text-xs text-chocolate-600 flex-1 truncate">{m.title}</span>
                        <span className="text-[10px] text-chocolate-400 flex-shrink-0">
                          {new Date(m.achieved_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function CoachProgressView({ userId, coachId }: { userId: string; coachId?: string }) {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [habits, setHabits] = useState<Habit[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [checkIns, setCheckIns] = useState<CheckIn[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [journals, setJournals] = useState<JournalEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      fetchGoalsForUser(userId),
      fetchHabitsForUser(userId),
      fetchTasksForUser(userId),
      fetchCheckInsForUser(userId, 30),
      fetchMilestonesForUser(userId),
      fetchJournalEntriesForUser(userId, 5),
    ]).then(([g, h, t, c, m, j]) => {
      setGoals(g);
      setHabits(h);
      setTasks(t);
      setCheckIns(c);
      setMilestones(m);
      setJournals(j);
    }).finally(() => setIsLoading(false));
  }, [userId]);

  if (isLoading) return <LoadingSpinner message="Loading progress..." />;

  const activeGoals = goals.filter(g => g.status === 'active');
  const completedGoals = goals.filter(g => g.status === 'completed');
  const pausedGoals = goals.filter(g => g.status === 'paused');
  const abandonedGoals = goals.filter(g => g.status === 'abandoned');
  const openTasks = tasks.filter(t => t.status !== 'done');
  const doneTasks = tasks.filter(t => t.status === 'done');
  const recentCheckIn = checkIns[0];

  const goalMap = new Map(goals.map(g => [g.id, g]));
  const goalMilestonesMap = new Map<string, Milestone[]>();
  for (const m of milestones) {
    if (m.goal_id) {
      const arr = goalMilestonesMap.get(m.goal_id) || [];
      arr.push(m);
      goalMilestonesMap.set(m.goal_id, arr);
    }
  }
  const unlinkedMilestones = milestones.filter(m => !m.goal_id);

  return (
    <div className="space-y-6">
      {/* Collaborative Outcomes */}
      {coachId && <CoachOutcomesSection userId={userId} coachId={coachId} />}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Active Goals', value: activeGoals.length, icon: Target, color: 'bg-sage-100 text-sage-600' },
          { label: 'Active Habits', value: habits.length, icon: Activity, color: 'bg-coral-100 text-coral-600' },
          { label: 'Open Tasks', value: openTasks.length, icon: ClipboardCheck, color: 'bg-amber-100 text-amber-600' },
          { label: 'Milestones', value: milestones.length, icon: Trophy, color: 'bg-yellow-100 text-yellow-600' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="freud-card-elevated p-4">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${color}`}>
              <Icon className="w-4 h-4" />
            </div>
            <p className="text-2xl font-bold text-chocolate-900">{value}</p>
            <p className="text-xs text-chocolate-500">{label}</p>
          </div>
        ))}
      </div>

      {/* Check-in history */}
      {checkIns.length > 0 && (
        <div className="freud-card-elevated p-6">
          <h3 className="freud-heading-lg text-chocolate-900 mb-4">Check-in History (last 30)</h3>
          <div className="mb-4 flex items-end gap-1 h-16">
            {checkIns.slice().reverse().map((c, i) => {
              const avg = (c.energy_level + c.mood_level + c.focus_level) / 3;
              const pct = Math.round((avg / 10) * 100);
              return (
                <div
                  key={i}
                  className="flex-1 bg-sage-400 rounded-t-sm transition-all"
                  style={{ height: `${pct}%` }}
                  title={`${new Date(c.checked_in_at).toLocaleDateString()} — avg ${avg.toFixed(1)}/10`}
                />
              );
            })}
          </div>
          {recentCheckIn && (
            <>
              <p className="text-xs text-chocolate-400 mb-3">
                Latest: {new Date(recentCheckIn.checked_in_at).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
              </p>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: 'Energy', value: recentCheckIn.energy_level, color: 'bg-amber-400' },
                  { label: 'Mood', value: recentCheckIn.mood_level, color: 'bg-coral-400' },
                  { label: 'Focus', value: recentCheckIn.focus_level, color: 'bg-sage-400' },
                ].map(({ label, value, color }) => (
                  <div key={label}>
                    <div className="flex justify-between mb-1">
                      <span className="text-xs text-chocolate-500">{label}</span>
                      <span className="text-xs font-bold text-chocolate-700">{value}/10</span>
                    </div>
                    <div className="h-2 bg-cream-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${color}`} style={{ width: `${value * 10}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              {recentCheckIn.notes && <p className="mt-3 text-sm text-chocolate-500 italic">"{recentCheckIn.notes}"</p>}
            </>
          )}
        </div>
      )}

      {/* Goals with nested milestones — full view grouped by status */}
      <div className="freud-card-elevated p-6">
        <div className="flex items-center gap-2 mb-4">
          <Target className="w-4 h-4 text-sage-600" />
          <h3 className="freud-heading-lg text-chocolate-900">Goals & Milestones</h3>
        </div>
        {goals.length === 0 ? (
          <p className="text-sm text-chocolate-400">No goals set yet.</p>
        ) : (
          <div className="space-y-5">
            <GoalGroup title="Active Goals" goals={activeGoals} goalMilestonesMap={goalMilestonesMap} defaultOpen={true} icon={Target} iconColor="text-sage-600" />
            <GoalGroup title="Completed Achievements" goals={completedGoals} goalMilestonesMap={goalMilestonesMap} defaultOpen={true} icon={Trophy} iconColor="text-blue-600" />
            <GoalGroup title="Paused" goals={pausedGoals} goalMilestonesMap={goalMilestonesMap} defaultOpen={false} icon={Clock} iconColor="text-amber-600" />
            <GoalGroup title="Abandoned" goals={abandonedGoals} goalMilestonesMap={goalMilestonesMap} defaultOpen={false} icon={X} iconColor="text-red-400" />
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Habits — full list */}
        <div className="freud-card-elevated p-6">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-coral-600" />
            <h3 className="freud-heading-lg text-chocolate-900">Habits</h3>
          </div>
          {habits.length === 0 ? (
            <p className="text-sm text-chocolate-400">No habits tracked yet.</p>
          ) : (
            <div className="space-y-3">
              {habits.map(h => {
                const thisWeek = (h.completions || []).length;
                const pct = Math.min((thisWeek / h.target_days_per_week) * 100, 100);
                return (
                  <div key={h.id}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-chocolate-700 truncate">{h.title}</span>
                      <span className="text-xs text-chocolate-400 flex-shrink-0">{thisWeek}/{h.target_days_per_week}d</span>
                    </div>
                    <div className="h-1.5 bg-cream-200 rounded-full overflow-hidden">
                      <div className="h-full bg-coral-400 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Tasks */}
        <div className="freud-card-elevated p-6">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardCheck className="w-4 h-4 text-amber-600" />
            <h3 className="freud-heading-lg text-chocolate-900">Tasks</h3>
          </div>
          {tasks.length === 0 ? (
            <p className="text-sm text-chocolate-400">No tasks yet.</p>
          ) : (
            <div className="space-y-2">
              {openTasks.map(t => (
                <div key={t.id} className="flex items-center gap-2">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                    t.status === 'in_progress' ? 'bg-amber-100 text-amber-700' : 'bg-cream-200 text-chocolate-500'
                  }`}>{t.status === 'in_progress' ? 'In Progress' : 'To Do'}</span>
                  <span className="text-sm text-chocolate-700 truncate">{t.title}</span>
                </div>
              ))}
              {doneTasks.length > 0 && (
                <p className="text-xs text-chocolate-400 pt-1">{doneTasks.length} tasks completed</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* All milestones with linked goal names — full list */}
      <div className="freud-card-elevated p-6">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-4 h-4 text-yellow-600" />
          <h3 className="freud-heading-lg text-chocolate-900">All Milestones</h3>
        </div>
        {milestones.length === 0 ? (
          <p className="text-sm text-chocolate-400">No milestones yet.</p>
        ) : (
          <div className="space-y-2">
            {milestones.map(m => (
              <div key={m.id} className="flex items-start gap-2">
                <Trophy className="w-3.5 h-3.5 text-yellow-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-chocolate-700">{m.title}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-[11px] text-chocolate-400">{new Date(m.achieved_at).toLocaleDateString()}</p>
                    {m.goal_id && goalMap.has(m.goal_id) && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-sage-600 bg-sage-50 px-1.5 py-0.5 rounded">
                        <Target className="w-3 h-3" />
                        {goalMap.get(m.goal_id)!.title}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Journal entries */}
      {journals.length > 0 && (
        <div className="freud-card-elevated p-6">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-4 h-4 text-chocolate-600" />
            <h3 className="freud-heading-lg text-chocolate-900">Recent Journal Entries</h3>
          </div>
          <div className="space-y-3">
            {journals.map(j => (
              <div key={j.id} className="border-l-2 border-cream-300 pl-4">
                <div className="flex items-center justify-between mb-0.5">
                  <p className="text-sm font-medium text-chocolate-800">{j.title}</p>
                  <p className="text-[11px] text-chocolate-400">{new Date(j.created_at).toLocaleDateString()}</p>
                </div>
                <p className="text-xs text-chocolate-500 line-clamp-2">{j.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
